import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import SnProductMappingSideMsg from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingSideMsg';
import {
  buildSnProductMappingColumns,
} from '#/modules/crmMgmt/aftersales/snProductMapping/data/snProductMappingColumns';
import {
  getSnProductMappingApiErrorMessage,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/apiError';
import useSnProductMappingList from '#/modules/crmMgmt/aftersales/snProductMapping/hooks/useSnProductMappingList';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const SN_PRODUCT_MAPPING_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  productCode: '產品代碼',
  productName: '產品名稱',
  status: '狀態',
};

const SnProductMappingListPage = () => {
  const canCreate = useCan(PERM.SN_PRODUCT_MAPPING_CREATE);
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useSnProductMappingList();

  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    customerLabelCache,
  ]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildSnProductMappingColumns({
        filters: listQuery.filters || {},
        onFilterChange: setFilter,
        customerLabelCache,
      });
    },
    [listQuery.filters, setFilter, customerLabelCache, labelCacheVersion],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  useEffect(() => {
    if (!displayData?.id) {
      return;
    }
    const fresh = rows.find((row) => row.id === displayData.id);
    if (!fresh) {
      return;
    }
    const sameStatus = fresh.status === displayData.status;
    const sameUpdatedAt = fresh.record?.updatedAt === displayData.record?.updatedAt;
    if (sameStatus && sameUpdatedAt) {
      return;
    }
    setSelectedData(fresh);
  }, [rows, displayData, setSelectedData]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate
    ? <ListAddButton to={ROUTES.snProductMappingCreate} />
    : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <AftersalesOpsPageHeader page="snProductMapping" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getSnProductMappingApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋產品代碼或產品名稱"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={SN_PRODUCT_MAPPING_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>
          <SnProductMappingSideMsg
            displayData={displayData}
            onRefetch={refetch}
            onDeleted={() => setSelectedData(null)}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default SnProductMappingListPage;
